// src/components/Navbar.jsx
// 导航栏组件 - 显示菜单和用户状态

import React, { useEffect, useState } from "react";
import { User, LogOut, Settings, Menu, X } from "lucide-react";
import { isAuthenticated, clearAuth, getUser } from "../utils/auth";

/**
 * Navbar: 兼容两种使用场景：
 * - 父组件传入 setCurrentView：作为 SPA 内部导航（不改动）
 * - 没有 setCurrentView（比如新开独立页面）：先验证登录状态（/api/me），再使用 window.location.href 导航
 */
const Navbar = ({ currentView, setCurrentView }) => {
  const [authed, setAuthed] = useState(() => {
    try {
      return !!isAuthenticated();
    } catch {
      return false;
    }
  });
  const [authReady, setAuthReady] = useState(!!authed);
  const [userInfo, setUserInfo] = useState(() => {
    try {
      return getUser();
    } catch {
      return null;
    }
  });

  // 映射 view -> URL（在独立页面使用）
  const toUrlByView = (view) => {
    switch (view) {
      case "calculator":
        return "/bazi";
      case "saved":
        return "/saved";
      case "login":
        return "/login";
      case "register":
        return "/register";
      case "userinfo":
        return "/yunying/userinfo";
      case "yunying":
        return "/yunying/yunying";
      default:
        return "/";
    }
  };

  // 在独立页面中进一步校验用户信息（若初始 isAuthenticated 返回 false）
  useEffect(() => {
    if (authReady) return;
    // 若已通过 utils/auth 判断为未登录，尝试请求后端 /api/me（带上 cookie）
    const tryFetchMe = async () => {
      try {
        const res = await fetch("/api/me", { credentials: "include" });
        if (res.ok) {
          const userData = await res.json();
          // 有效 session
          setAuthed(true);
          setUserInfo(userData.user);
        } else {
          setAuthed(false);
          setUserInfo(null);
        }
      } catch (err) {
        setAuthed(false);
        setUserInfo(null);
      } finally {
        setAuthReady(true);
      }
    };
    tryFetchMe();
  }, [authReady]);

  const handleNavClick = (view) => {
    // 点击登录/注册时先记录当前页面，便于登录后返回
    if (view === "login" || view === "register") {
      try {
        const returnInfo = {
          path: window.location.pathname || "/",
          search: window.location.search || "",
          ts: Date.now(),
        };
        sessionStorage.setItem("post_login_return", JSON.stringify(returnInfo));
      } catch (e) {
        // ignore sessionStorage 错误
      }
    }

     window.location.href = toUrlByView(view);
  };

  const handleLogout = () => {
    clearAuth();
    if (typeof setCurrentView === "function") {
      setCurrentView("calculator");
    } else {
      window.location.href = "/";
    }
    // 确保全局状态刷新
    window.location.reload();
  };

  const [isMenuOpen, setIsMenuOpen] = useState(false);

  return (
    <nav className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white shadow-lg relative">
      <div className="max-w-7xl mx-auto px-4">
        <div className="flex justify-between items-center h-16">
          {/* 左侧 - Logo和标题（点击回到首页 Index.jsx） */}
          <div
            className="flex items-center space-x-4 cursor-pointer"
            onClick={() => {
              // 在 SPA 中若有 setCurrentView 也可同步（可选）
              // 这里以直接导航到首页为主，保证在独立页面中也生效
              window.location.href = "/";
            }}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") window.location.href = "/";
            }}
          >
            <img
              src="/yuanyun_logo.jpg"
              alt="圆運logo"
              className="h-12 w-12 object-cover rounded-full"
            />
            <h1 className="text-xl font-bold">「圆運」易学系统</h1>
          </div>

          {/* 中间 - 主要导航菜单 */}
          <div className="hidden md:flex space-x-8">
            {/* 其它导航（如有需要可在此添加） */}
          </div>

          {/* 右侧 - 用户菜单 (桌面端) */}
          <div className="hidden md:flex items-center space-x-4">
            {authed ? (
              <>
                <a
                  href="/portal"
                  className="hover:text-gray-200 transition-colors"
                >
                  广场
                </a>
                <a
                  href="/"
                  className="hover:text-gray-200 transition-colors"
                >
                  排盘
                </a>              
                {getUser()?.is_admin && (
                  <button
                    onClick={() => handleNavClick("yunying")}
                    className="flex items-center space-x-1 hover:text-gray-200 transition-colors"
                  >
                    <Settings size={20} />
                    <span className="hidden md:inline">管理员入口</span>
                  </button>
                )}

                <button
                  onClick={() => handleNavClick("userinfo")}
                  className="flex items-center space-x-1 hover:text-gray-200 transition-colors"
                >
                  <User size={20} />
                  <span className="hidden md:inline">{userInfo?.nickname || '个人中心'}</span>
                </button>
                <button
                  onClick={handleLogout}
                  className="flex items-center space-x-2 bg-white bg-opacity-20 hover:bg-opacity-30 px-3 py-2 rounded-md text-sm font-medium transition-colors"
                >
                  <LogOut className="h-4 w-4" />
                  <span className="hidden sm:block">退出</span>
                </button>
              </>
            ) : (
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => handleNavClick("login")}
                  className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                    currentView === "login"
                      ? "bg-white text-indigo-600"
                      : "text-white bg-white bg-opacity-20 hover:bg-opacity-30"
                  }`}
                >
                  登录
                </button>
                <button
                  onClick={() => handleNavClick("register")}
                  className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                    currentView === "register"
                      ? "bg-white text-indigo-600"
                      : "text-white bg-white bg-opacity-20 hover:bg-opacity-30"
                  }`}
                >
                  注册
                </button>
                <a
                  href="/portal"
                  className="px-4 py-2 text-sm font-medium text-white hover:text-gray-200 transition-colors"
                >
                  发布广场
                </a>
                <a
                  href="/"
                  className="px-4 py-2 text-sm font-medium text-white hover:text-gray-200 transition-colors"
                >
                  排盘
                </a>
              </div>
            )}
          </div>

          {/* 移动端菜单按钮 */}
          <div className="md:hidden flex items-center">
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="text-white hover:text-gray-200 focus:outline-none"
            >
              {isMenuOpen ? <X size={28} /> : <Menu size={28} />}
            </button>
          </div>
        </div>
      </div>

      {/* 移动端下拉菜单 */}
      {isMenuOpen && (
        <div className="md:hidden absolute top-16 left-0 w-full bg-indigo-700 shadow-xl z-50">
          <div className="px-4 pt-2 pb-4 space-y-2 flex flex-col">
            {authed ? (
              <>
                <a
                  href="/portal"
                  className="block px-3 py-2 rounded-md text-base font-medium text-white hover:bg-indigo-600 transition-colors"
                >
                  广场
                </a>
                <a
                  href="/"
                  className="block px-3 py-2 rounded-md text-base font-medium text-white hover:bg-indigo-600 transition-colors"
                >
                  排盘
                </a>
                {getUser()?.is_admin && (
                  <button
                    onClick={() => {
                        handleNavClick("yunying");
                        setIsMenuOpen(false);
                    }}
                    className="flex w-full items-center space-x-2 px-3 py-2 rounded-md text-base font-medium text-white hover:bg-indigo-600 transition-colors"
                  >
                    <Settings size={20} />
                    <span>管理员入口</span>
                  </button>
                )}
                <button
                  onClick={() => {
                      handleNavClick("userinfo");
                      setIsMenuOpen(false);
                  }}
                  className="flex w-full items-center space-x-2 px-3 py-2 rounded-md text-base font-medium text-white hover:bg-indigo-600 transition-colors"
                >
                  <User size={20} />
                  <span>{userInfo?.nickname || '个人中心'}</span>
                </button>
                <button
                  onClick={handleLogout}
                  className="flex w-full items-center space-x-2 px-3 py-2 rounded-md text-base font-medium text-white bg-indigo-800 hover:bg-indigo-600 transition-colors"
                >
                  <LogOut size={20} />
                  <span>退出登录</span>
                </button>
              </>
            ) : (
              <>
                <button
                  onClick={() => {
                      handleNavClick("login");
                      setIsMenuOpen(false);
                  }}
                  className={`block w-full text-left px-3 py-2 rounded-md text-base font-medium transition-colors ${
                    currentView === "login"
                      ? "bg-white text-indigo-600"
                      : "text-white hover:bg-indigo-600"
                  }`}
                >
                  登录
                </button>
                <button
                  onClick={() => {
                    handleNavClick("register");
                    setIsMenuOpen(false);
                  }}
                  className={`block w-full text-left px-3 py-2 rounded-md text-base font-medium transition-colors ${
                    currentView === "register"
                      ? "bg-white text-indigo-600"
                      : "text-white hover:bg-indigo-600"
                  }`}
                >
                  注册
                </button>
                <a
                  href="/portal"
                  className="block px-3 py-2 rounded-md text-base font-medium text-white hover:bg-indigo-600 transition-colors"
                >
                  发布广场
                </a>
                <a
                  href="/"
                  className="block px-3 py-2 rounded-md text-base font-medium text-white hover:bg-indigo-600 transition-colors"
                >
                  排盘
                </a>
              </>
            )}
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;
